import { ServerProduct, ServerStock } from '@/lib/janitsjar/Product';
import type { FagmoblerInventory } from '@fagmobler/types';
import { useStores } from '@/components/store';
import { OmniumStore } from '@fagmobler/omnium/src/types';
import { Point } from 'geojson';
import turfDistance from '@turf/distance';

export type ArrayElement<ArrayType extends readonly unknown[]> =
  ArrayType extends readonly (infer ElementType)[] ? ElementType : never;

export const getDistance = (store: OmniumStore, currentStore: OmniumStore) => {
  if (!store.longitude || !store.latitude) return null;
  if (!currentStore.longitude || !currentStore.latitude) return null;

  const position: Point = {
    coordinates: [
      parseFloat(currentStore.latitude),
      parseFloat(currentStore.longitude),
    ],
    type: 'Point',
  };

  const storePosition: Point = {
    coordinates: [parseFloat(store.latitude), parseFloat(store.longitude)],
    type: 'Point',
  };

  return turfDistance(position, storePosition, {
    units: 'kilometers',
  });
};

export const serverProductToFagmoblerInventory = (
  product: ServerProduct
): FagmoblerInventory => {
  return (
    product.stock?.reduce((acc, stockItem) => {
      return {
        ...acc,
        [product.sku]: {
          [stockItem.storeId]: {
            display: stockItem.display,
            stock: stockItem.stock,
          },
        },
      };
    }, {}) || {}
  );
};

export const useCurrentStoreStock = (stock: ServerStock) => {
  const currentStore = useCurrentStore();

  if (!currentStore) return null;

  return stock.find((s) => s.storeId === currentStore.id) || null;
};

export const useCurrentStore = () => {
  const { current: currentStoreQuery } = useStores();
  const currentStore = currentStoreQuery.data?.store;

  return currentStore ?? null;
};

export const useStoresData = () => {
  const { stores: storesQuery } = useStores();
  return storesQuery.data ?? [];
};

export const useStockByDistance = (product: ServerProduct) => {
  const currentStore = useCurrentStore();
  const stores = useStoresData();

  return stores
    .filter((store) => store.latitude && store.longitude)
    .map((store) => ({
      ...store,
      distance: getDistance(store, currentStore ?? stores[0]) || undefined,
    }))
    .map((store) => {
      const stock = product.stock?.find((s) => s.storeId === store.id);
      return {
        ...store,
        stock,
      };
    })
    .sort((a, b) => {
      if (a.distance == null && b.distance == null) return 0;
      if (a.distance == null) return -1;
      if (b.distance == null) return 1;
      return a.distance > b.distance ? 1 : -1;
    })
    .filter(
      (store) =>
        store.id === currentStore?.id ||
        (store.stock && (store.stock.stock > 0 || store.stock.display > 0))
    );
};
